import React, { useState, useContext, useEffect, useRef } from 'react';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { getToken } from '../../../api/Account';
import { DataContext } from '../../../context/DataContext';
import { getRuta, simpleMessage } from '../../../helpers/Helpers';
import { formatearFecha } from '../../../helpers/formato';
import { ConfirmDialog } from '../../../components/base';
import {
	Alert,
	Container,
	Stack,
	Paper,
	FormControl,
	Select,
	MenuItem,
	InputLabel,
	TextField,
	Button,
	Typography
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCodePullRequest } from '@fortawesome/free-solid-svg-icons';

import { getPlazoByPeriodicidadAsync } from '../../../api/Plazo';
import { getPeriodicidadListAsync } from '../../../api/Periodicidad';
import { getInteresListAsync } from '../../../api/InteresCorriente';
import { reestructurarCreditAsync } from '../../../api/Credits';
import { useNavigate } from 'react-router-dom';

const Reestructuracion = ({ id, clientName, setShowModal }) => {
	const [fechaPrimeraCuota, setFechaPrimeraCuota] = useState(new Date());
	const [periodicidadList, setPeriodicidadList] = useState([]);
	const [selectedPeriodicidad, setSelectedPeriodicidad] = useState('');

	const [plazoList, setPlazoList] = useState([]);
	const [selectedPlazo, setSelectedPlazo] = useState('');

	const [interesCorrienteList, setInteresCorrienteList] = useState([]);
	const [selectedInteresCorriente, setSelectedInteresCorriente] = useState('');

	// Reestructurar cancela este crédito y crea otro: la llamada sale una sola vez, solo tras confirmar.
	// El ref corta el segundo clic al instante (el estado tarda un render); `cargando` bloquea el diálogo.
	const enviando = useRef(false);
	const [confirmando, setConfirmando] = useState(false);
	const [cargando, setCargando] = useState(false);

	const { setIsLoading, setIsUnautorized } = useContext(DataContext);
	let ruta = getRuta();
	const token = getToken();
	let navigate = useNavigate();

	useEffect(() => {
		getPeriodicidad();
		getTazaInteres();
	}, []);

	const getPeriodicidad = async () => {
		setIsLoading(true);
		const resultPer = await getPeriodicidadListAsync(token);
		if (!resultPer.statusResponse) {
			setIsLoading(false);
			if (resultPer.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(resultPer.error, 'error');
			return;
		}
		setPeriodicidadList(resultPer.data.result);
		setIsLoading(false);
	};

	const getTazaInteres = async () => {
		setIsLoading(true);
		const resultIC = await getInteresListAsync(token);
		if (!resultIC.statusResponse) {
			setIsLoading(false);
			if (resultIC.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(resultIC.error, 'error');
			return;
		}
		setInteresCorrienteList(resultIC.data.result);
		setIsLoading(false);
	};

	const handleChangePeriodicidad = async (event) => {
		const selected = periodicidadList.filter((item) => item.id == event.target.value);
		let dias = selected[0].cuotaDias;

		var newDate = new Date();
		newDate.setDate(newDate.getDate() + dias);

		setFechaPrimeraCuota(newDate);
		setPlazoList([]);
		setSelectedPeriodicidad(event.target.value);

		if (event.target.value !== '') {
			setIsLoading(true);
			const result = await getPlazoByPeriodicidadAsync(token, event.target.value);
			if (!result.statusResponse) {
				setIsLoading(false);
				if (result.error === 'Unauthorized') {
					setIsUnautorized(true);
					return;
				}
				simpleMessage(result.error, 'error');
				return;
			}
			setPlazoList(result.data.result);
			setIsLoading(false);
		} else {
			setPlazoList([]);
		}
	};

	// Primer paso: las mismas validaciones de siempre y, solo si pasan, se pide confirmar.
	const pedirConfirmacion = () => {
		if (validate()) {
			setConfirmando(true);
		}
	};

	// Segundo paso, solo desde "Reestructurar" del diálogo: una llamada por confirmación.
	const saveChanges = async () => {
		if (enviando.current) return;
		enviando.current = true;
		setCargando(true);
		const datos = {
			creditId: id,
			periocidadId: selectedPeriodicidad,
			plazoId: selectedPlazo,
			interesCorrienteId: selectedInteresCorriente,
			fechaPrimeraCuota
		};
		setIsLoading(true);
		let result;
		try {
			result = await reestructurarCreditAsync(token, datos);
		} catch (error) {
			// Red caída o timeout: la API lanza en vez de devolver `statusResponse: false`. Con un timeout el
			// servidor pudo reestructurar igual, así que se avisa que revisen antes de reintentar.
			setIsLoading(false);
			enviando.current = false;
			setCargando(false);
			setConfirmando(false);
			simpleMessage(
				'No se pudo confirmar la reestructuración. Revisa la lista de créditos antes de reintentar: el servidor pudo haberla hecho.',
				'error'
			);
			return;
		}
		if (!result.statusResponse) {
			setIsLoading(false);
			enviando.current = false;
			setCargando(false);
			setConfirmando(false);
			if (result.error === 'Unauthorized') {
				setIsUnautorized(true);
				return;
			}
			simpleMessage(result.error, 'error');
			return;
		}

		// Reestructurado: el candado se queda puesto, ya no hay nada más que enviar.
		navigate(`${ruta}/credits/`);
		setIsLoading(false);
		setConfirmando(false);
		setShowModal(false);
		simpleMessage('Crédito reestructurado...!', 'success');
	};

	const descripcionDe = (lista, valor, texto) => {
		const item = lista.find((x) => String(x.id) === String(valor));
		return item ? texto(item) : '—';
	};

	const validate = () => {
		let isValid = true;

		if (fechaPrimeraCuota === '') {
			simpleMessage('Debe ingresar fecha de primera cuota', 'error');
			return (isValid = false);
		}

		if (selectedPlazo === '') {
			simpleMessage('Debe seleccionar un plazo', 'error');
			return (isValid = false);
		}

		if (selectedInteresCorriente === '') {
			simpleMessage('Debe seleccionar una tasa de interes', 'error');
			return (isValid = false);
		}

		return isValid;
	};

	return (
		<div>
			<Container sx={{ width: { xs: '100%', sm: 550 } }}>
				<Paper
					style={{
						padding: 20
					}}
				>
					<Stack spacing={2}>
						<Alert
							severity="warning"
							sx={(theme) => ({
								backgroundColor: theme.palette.soft?.warning,
								color: theme.palette.text.primary,
								border: `1px solid ${theme.palette.warning.main}`,
								'& .MuiAlert-icon': { color: theme.palette.warning.main }
							})}
						>
							Al guardar, este crédito se cancela y se crea uno nuevo con estas condiciones.
						</Alert>

						<DesktopDatePicker
							closeOnSelect
							label="Fecha de primera cuota"
							inputFormat="DD/MM/YYYY"
							value={fechaPrimeraCuota}
							onChange={(newValue) => setFechaPrimeraCuota(newValue)}
							renderInput={(params) => <TextField {...params} fullWidth />}
						/>

						<FormControl fullWidth style={{ textAlign: 'left' }}>
							<InputLabel id="reestructura-periodicidad-label">Periodicidad</InputLabel>
							<Select
								labelId="reestructura-periodicidad-label"
								id="reestructura-periodicidad"
								onChange={handleChangePeriodicidad}
								value={selectedPeriodicidad}
								label="Periodicidad"
								style={{ textAlign: 'left' }}
							>
								<MenuItem value="">
									<em>Seleccione un Periodo</em>
								</MenuItem>
								{periodicidadList.map((item) => {
									return (
										<MenuItem key={item.id} value={item.id}>
											{item.description}
										</MenuItem>
									);
								})}
							</Select>
						</FormControl>

						<FormControl fullWidth style={{ textAlign: 'left' }}>
							<InputLabel id="reestructura-plazo-label">Plazo</InputLabel>
							<Select
								labelId="reestructura-plazo-label"
								id="reestructura-plazo"
								onChange={(e) => setSelectedPlazo(e.target.value)}
								value={selectedPlazo}
								label="Plazo"
								style={{ textAlign: 'left' }}
							>
								<MenuItem value="">
									<em>Seleccione un Plazo</em>
								</MenuItem>
								{plazoList.map((item) => {
									return (
										<MenuItem key={item.id} value={item.id}>
											{item.description}
										</MenuItem>
									);
								})}
							</Select>
						</FormControl>

						<FormControl fullWidth style={{ textAlign: 'left' }}>
							<InputLabel id="reestructura-tasa-label">Tasa de interés</InputLabel>
							<Select
								labelId="reestructura-tasa-label"
								id="reestructura-tasa"
								onChange={(e) => setSelectedInteresCorriente(e.target.value)}
								value={selectedInteresCorriente}
								label="Tasa de interés"
								style={{ textAlign: 'left' }}
							>
								<MenuItem value="">
									<em>Seleccione una Tasa de Interes</em>
								</MenuItem>
								{interesCorrienteList.map((item) => {
									return (
										<MenuItem key={item.id} value={item.id}>
											{`${item.intCorriente.toFixed(2)} %`}
										</MenuItem>
									);
								})}
							</Select>
						</FormControl>

						<Button
							variant="contained"
							color="error"
							startIcon={<FontAwesomeIcon icon={faCodePullRequest} />}
							fullWidth
							onClick={pedirConfirmacion}
							size="large"
						>
							Reestructurar crédito
						</Button>
					</Stack>
				</Paper>
			</Container>

			<ConfirmDialog
				abierto={confirmando}
				peligro
				cargando={cargando}
				titulo="Reestructurar crédito"
				mensaje={
					<>
						<Typography component="p" sx={{ mb: 1 }}>
							{`Se cancelará el crédito #${id}${clientName ? ` de ${clientName}` : ''} y se creará otro con su saldo pendiente y estas condiciones:`}
						</Typography>
						<Stack component="ul" spacing={0.5} sx={{ m: 0, mb: 1, pl: 2.5 }}>
							<li>
								{`Periodicidad: ${descripcionDe(
									periodicidadList,
									selectedPeriodicidad,
									(x) => x.description
								)}`}
							</li>
							<li>
								{`Plazo: ${descripcionDe(plazoList, selectedPlazo, (x) => x.description)}`}
							</li>
							<li>
								{`Tasa de interés: ${descripcionDe(
									interesCorrienteList,
									selectedInteresCorriente,
									(x) => `${x.intCorriente.toFixed(2)} %`
								)}`}
							</li>
							<li>{`Primera cuota: ${formatearFecha(fechaPrimeraCuota)}`}</li>
						</Stack>
						<Typography component="p" sx={{ color: 'text.secondary' }}>
							Revisa las condiciones antes de continuar: una vez hecha, el crédito original queda
							cancelado.
						</Typography>
					</>
				}
				textoConfirmar="Reestructurar"
				onConfirmar={saveChanges}
				onCancelar={() => setConfirmando(false)}
			/>
		</div>
	);
};

export default Reestructuracion;
